/**
 * CSV de pares de Q&A — o formato de ida e volta com o ChatData.
 *
 * As duas direções moram juntas de propósito: exportar e importar têm que
 * concordar sobre a mesma coisa, e quando moram em arquivos diferentes é sempre
 * uma das duas que muda sozinha.
 *
 * Colunas: `question,answer,folder`, que é exatamente o que o export do ChatData
 * entrega (medido num arquivo real de 219 linhas, 10/09/2026). `folder` vem vazia
 * de lá; nós a usamos para as tags, separadas por `;`, porque vírgula já é o
 * separador de coluna.
 */

// Um parser de verdade, e não `split(',')`: no arquivo real do ChatData as
// respostas têm vírgula, aspas e quebra de linha DENTRO do campo. `split`
// devolveria pedaços de frase como se fossem colunas, e o pior é que devolveria
// sem erro — o operador veria 219 linhas importadas e conteúdo picado.
function lerLinhas(texto) {
  const linhas = [];
  let campo = '';
  let linha = [];
  let dentroDeAspas = false;

  for (let i = 0; i < texto.length; i += 1) {
    const c = texto[i];
    if (dentroDeAspas) {
      if (c !== '"') {
        campo += c;
      } else if (texto[i + 1] === '"') {
        // `""` é uma aspa literal dentro do campo, não o fim dele.
        campo += '"';
        i += 1;
      } else {
        dentroDeAspas = false;
      }
    } else if (c === '"') {
      dentroDeAspas = true;
    } else if (c === ',') {
      linha.push(campo);
      campo = '';
    } else if (c === '\n' || c === '\r') {
      // CRLF conta como UMA quebra; e linha vazia no fim do arquivo não é linha.
      if (c === '\r' && texto[i + 1] === '\n') i += 1;
      linha.push(campo);
      if (linha.some(v => v !== '')) linhas.push(linha);
      linha = [];
      campo = '';
    } else {
      campo += c;
    }
  }
  linha.push(campo);
  if (linha.some(v => v !== '')) linhas.push(linha);
  return linhas;
}

// O export do ChatData vem com `•` + tab grudado no começo de perguntas coladas
// de Word, e com espaço duplo no meio. Medido: 14 das 219 linhas. Limpar aqui é o
// que impede a sujeira de virar embedding.
function limpaTexto(s) {
  return (s || '').replace(/[•\t]/g, ' ').replace(/\s+/g, ' ').trim();
}

/**
 * Lê um CSV de Q&A e devolve os pares. Nunca levanta: arquivo torto devolve o
 * que deu para entender, e quem chama decide o que fazer com lista vazia.
 *
 * Aceita as colunas em qualquer ordem, pelo cabeçalho. Sem cabeçalho
 * reconhecível, assume `question,answer,folder` pela posição — é o formato do
 * ChatData, e recusar um arquivo sem cabeçalho só faria o operador editá-lo à mão.
 */
export function lerCsvQa(texto) {
  const limpo = (texto || '').replace(/^\uFEFF/, ''); // BOM do Excel
  const linhas = lerLinhas(limpo);
  if (!linhas.length) return [];

  const cabecalho = linhas[0].map(c => c.trim().toLowerCase());
  const temCabecalho =
    cabecalho.includes('question') || cabecalho.includes('pergunta');
  const col = nomes => {
    const i = cabecalho.findIndex(c => nomes.includes(c));
    return i === -1 ? null : i;
  };
  const iQ = temCabecalho ? col(['question', 'pergunta']) : 0;
  const iA = temCabecalho ? col(['answer', 'resposta']) : 1;
  const iF = temCabecalho ? col(['folder', 'pasta', 'tags']) : 2;

  return linhas
    .slice(temCabecalho ? 1 : 0)
    .map(l => ({
      question: limpaTexto(l[iQ] ?? ''),
      answer: limpaTexto(l[iA] ?? ''),
      tags: (l[iF] ?? '')
        .split(';')
        .map(t => t.trim())
        .filter(Boolean),
    }))
    .filter(p => p.question && p.answer);
}

function campoCsv(valor) {
  const s = String(valor ?? '');
  // Aspas sempre que houver separador, aspa ou quebra — e aspa interna dobrada.
  return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

/** Monta o CSV a partir dos pares, no mesmo formato que `lerCsvQa` lê. */
export function montarCsvQa(pares) {
  const linhas = [['question', 'answer', 'folder'].join(',')];
  (pares || []).forEach(p => {
    linhas.push(
      [
        campoCsv(p.question),
        campoCsv(p.answer),
        campoCsv((p.tags || []).join(';')),
      ].join(',')
    );
  });
  // `\r\n` porque é o que o Excel espera, e é o que o arquivo do ChatData usa.
  return `${linhas.join('\r\n')}\r\n`;
}
